import { useEffect, useState } from 'react';
import {
  useCreateConfigurationSettingsMutation,
  useDeployConfigurationSettingsMutation,
} from 'src/services';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { ProductsType } from 'src/constants/products';
import { PATH_ONBOARDING } from 'src/constants/spa-routes';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { Button } from 'src/components/ui/button';
import { CustomTemplateSelect } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CustomTemplateSelect/CustomTemplateSelect';
import { Skeleton } from 'src/components/ui/skeleton';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';
import type { CustomTemplateOption } from 'src/pages/ConfigurationPage/SystemPromptTab/SystemPromptTemplateSelect/SystemPromptTemplateSelect';
import type { ManualPromptFormValues } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CustomTemplateSelect/CustomTemplateSelect';
import type { ConfigurationPrompt, CustomTemplate } from 'src/services/configurationService';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const LoadingSkeleton = () => {
  return (
    <div className={'w-full h-full flex flex-col gap-5'}>
      <Skeleton className={'w-full flex h-14'} />
      <Skeleton className={'w-full flex h-80'} />
      <Skeleton className={'w-full flex h-14'} />
    </div>
  );
};

export const OnboardingStep4Manual = ({
  customTemplatesData,
  configurationSettingsPrompt,
}: {
  customTemplatesData?: CustomTemplate[];
  configurationSettingsPrompt?: ConfigurationPrompt;
}) => {
  const [customTemplateOptions, setCustomTemplateOptions] = useState<CustomTemplateOption[]>([]);
  const { project } = useAppSelector((s) => s.onboardingSlice);
  const [selectedTemplate, setSelectedTemplate] = useState<CustomTemplateOption | null>(null);
  const navigate = useNavigate();
  const [initialPlateValue, setInitialPlateValue] = useState('');
  const [
    createConfigurationSettings,
    { data: createConfigurationSettingsData, isLoading: isLoadingCreateConfigurationSettings },
  ] = useCreateConfigurationSettingsMutation();
  const [deployConfigurationSettings, { isLoading: isLoadingDeployConfigurationSettings }] =
    useDeployConfigurationSettingsMutation();

  const { control, setValue, handleSubmit } = useForm<ManualPromptFormValues>({
    defaultValues: {
      text: '',
      template_id: '',
    },
    resolver: formResolver(
      object({
        text: stringScheme({ required: true }),
      }),
    ),
  });

  useLoaderWhile(isLoadingCreateConfigurationSettings, isLoadingDeployConfigurationSettings);

  useEffect(() => {
    if (customTemplatesData) {
      const options = customTemplatesData.map((customTemplate) => {
        return {
          id: `${customTemplate.id}`,
          name: customTemplate.name,
          placeholder: customTemplate.prompt_parts[0].placeholder,
          text: customTemplate.prompt_parts[0].template,
        };
      });

      setCustomTemplateOptions(options);

      if (configurationSettingsPrompt?.is_manual) {
        const promptText = configurationSettingsPrompt.text;
        const option = options.find((opt) => opt.text === promptText);

        if (option) {
          onCustomTemplateChange(option);
          setValue('template_id', `${configurationSettingsPrompt.template_id}`);

          return;
        } else {
          setValue('text', promptText);
          setInitialPlateValue(promptText);
          setValue('template_id', 'custom');

          return;
        }
      }
      if (options[0]) {
        onCustomTemplateChange(options[0]);
        setValue('template_id', options[0]?.id);
      }
    }
  }, [customTemplatesData, configurationSettingsPrompt]);

  useEffect(() => {
    if (createConfigurationSettingsData) {
      deployConfigurationSettings({
        project_id: project.id,
        settings_id: createConfigurationSettingsData.id,
      }).then(next);
    }
  }, [createConfigurationSettingsData]);

  const back = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/3?type=${ProductsType.customer}`, { replace: true });
  };

  const next = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/7?type=${ProductsType.customer}`, { replace: true });
  };

  const submit = ({ text }: { text: string }) => {
    let prompt;

    if (selectedTemplate) {
      prompt = {
        template_id: +selectedTemplate.id,
        input_data: {
          [selectedTemplate.placeholder]: text,
        },
      };
    } else if (configurationSettingsPrompt) {
      const placeholder = Object.keys(configurationSettingsPrompt.input_data)[0];

      prompt = {
        template_id: configurationSettingsPrompt.template_id,
        input_data: {
          [placeholder]: text,
        },
      };
    }

    createConfigurationSettings({ id: project.id, body: { prompt } });
  };

  const onCustomTemplateChange = (option?: CustomTemplateOption) => {
    if (option) {
      setValue('text', option.text);
      setInitialPlateValue(option.text);
      setSelectedTemplate(option);
    }
  };

  return isLoadingCreateConfigurationSettings || isLoadingDeployConfigurationSettings ? (
    <LoadingSkeleton />
  ) : (
    <>
      <div className={'flex flex-col w-full gap-[5px] py-1 h-full items-center'}>
        <form
          id={'onboarding-step-4-manual'}
          className={'flex flex-col w-full gap-5'}
          onSubmit={handleSubmit(submit)}
        >
          <div className={'flex flex-col gap-2.5'}>
            <CustomTemplateSelect
              control={control}
              label={'Template:'}
              options={customTemplateOptions}
              onChange={onCustomTemplateChange}
            />
          </div>
        </form>
        <Controller
          control={control}
          name="text"
          render={({ field: { onChange }, fieldState: { error } }) => (
            <div className={'flex flex-col w-full gap-2.5'}>
              <PlateEditor
                label={'System Prompt:'}
                initialValue={initialPlateValue}
                onChange={onChange}
                error={error}
                containerClassName={'max-h-[470px] overflow-hidden'}
                className={'h-[calc(100%-50px)]'}
              />
            </div>
          )}
        />
      </div>
      <div className={'mt-5 flex items-center justify-center gap-5'}>
        <div className={'flex flex-col w-full items-center justify-center gap-5'}>
          <Button form={'onboarding-step-4-manual'} className="w-full py-6 font-semibold">
            <p>Continue</p>
          </Button>
          <button
            type="button"
            onClick={back}
            className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
          >
            ← Back
          </button>
        </div>
      </div>
    </>
  );
};
